import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JTable } from '@atoms/JTable';
import CvVideoRow from '../CVVideoRow';
import { Box, Stack } from '@mui/material';
import { JTablePagination } from '@atoms/JTablePagination';
import { KEY_ORDER } from '@models/key-order';
import TableColumn from '@models/table-column';
import { useGetCVVideos } from '../../queries/useGetCVVideos';
import useGetCvVideosList from '../../hooks/useGetCvVideosList';
import { JSelect } from '@atoms/JSelect';
import { FilterCriteriaFields } from '@models/filter-criteria-fields';
import { JAutocomplete } from '@components/molucules/JAutocomplete';
import IOption from '@models/option';
import { useCities } from '@/queries/useCities';
import { ISearchFilter } from '@models/dto/searchCriteria.dto';
import { useDebouncedCallback } from 'use-debounce';
import { useCountries } from '@/queries/useCountries';
import { SearchTextField } from '@atoms/SearchTextField';
import CvVideoStatus from '../../models/cv-video-status';
import { useLocation } from 'react-router-dom';

const FRANCE_COUNTRY_ID = '192acde7-7ae8-4eac-a224-958be66f0ce7';

export const CV_VIDEO_TABLE_COLS: TableColumn[] = [
  { name: 'USER_FIRSTNAME', orderable: true, sortingField: 'user.firstname' },
  { name: 'USER_LASTNAME', orderable: true, sortingField: 'user.lastname' },
  {
    name: 'PROFILE_CREATION',
    orderable: true,
    sortingField: 'cvVideo.createdAt',
  },
  { name: 'CITY_ZIP_CODE', orderable: true, sortingField: 'city.name' },
  {
    name: 'CV_VIDEO_NAME',
    orderable: true,
    sortingField: 'cvVideo.name',
  },
  {
    name: 'CV_VIDEO_CREATION',
    orderable: true,
    sortingField: 'candidate.created_at',
  },
  { name: 'CV_VIDEO_STATUS', orderable: true, sortingField: 'cvVideo.status' },
];
const DEFAULT_SEARCH_FILTER: ISearchFilter[] = [
  { field: FilterCriteriaFields.CVVIDEO_STATUS, value: CvVideoStatus.PENDING },
];

const CV_VIDEO_PER_PAGE = 20;
const CV_VIDEO_DEFAULT_ORDER = {
  order: KEY_ORDER.DESC,
  orderBy: 'cvVideo.createdAt',
};

const CVVideoList: React.FC = () => {
  const [cityInputValue, setCityInputValue] = useState<string>('');
  const [countryInputValue, setCountryInputValue] = useState<string>('');
  const [countryId, setCountryId] = useState<string>('');
  const { hash } = useLocation();
  const [searchFilter, setSearchFilter] = useState<ISearchFilter[]>(
    DEFAULT_SEARCH_FILTER,
  );
  const { data: cities } = useCities(10, 1, cityInputValue);
  const { data: countries } = useCountries(10, 1, countryInputValue);
  const { t } = useTranslation();
  const { setCvVideosCount, setCvVideosPendingCount } = useGetCvVideosList();
  const [page, setPage] = useState<number>();
  const [order, setOrder] = useState<{
    order: KEY_ORDER;
    orderBy: string;
  }>(CV_VIDEO_DEFAULT_ORDER);

  const defaultStatusValue = hash?.substring(1);

  const { data } = useGetCVVideos(
    {
      page,
      limit: CV_VIDEO_PER_PAGE,
      order: order.order,
      orderBy: order.orderBy,
    },
    {
      searchCriteria: searchFilter,
    },
    defaultStatusValue ? defaultStatusValue === searchFilter[0].value : true,
  );
  const appendSearchFilter = (
    field: FilterCriteriaFields,
    value: string | undefined,
  ): void => {
    if (value === undefined) {
      setSearchFilter((prevValue) =>
        prevValue.filter((item) => item.field !== field),
      );
      return;
    }
    const existed = searchFilter?.some((item) => item.field === field);

    if (!existed) setSearchFilter([...searchFilter, { field, value }]);
    if (value === '')
      setSearchFilter((prevValue) =>
        prevValue.filter((item) => item.field !== field),
      );
    else
      setSearchFilter((prevValue) =>
        prevValue.map((item) =>
          item.field === field ? { field, value } : item,
        ),
      );
  };

  const debouncedCity = useDebouncedCallback((value) => {
    setCityInputValue(value);
  }, 1000);
  const debouncedCountry = useDebouncedCallback((value) => {
    setCountryInputValue(value);
  }, 1000);
  const debouncedAppendSearchFilter = useDebouncedCallback(
    appendSearchFilter,
    500,
  );
  const citiesOptions = cities
    ? cities.data.data.map(({ id, name, zipCode }) => {
        return { value: id, label: `${name} / ${zipCode}` };
      })
    : [];
  const countriesOptions = countries
    ? countries.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];
  const rows = data?.data.data;
  const meta = data?.data.meta;
  const pagesCount = data?.data.meta.pageCount;
  const isNotFound = rows?.length === 0;

  const handleColumnClick = (column: string) => {
    if (order.orderBy === column) {
      setOrder((prevOrder) => ({
        order:
          prevOrder.order === KEY_ORDER.ASC ? KEY_ORDER.DESC : KEY_ORDER.ASC,
        orderBy: column,
      }));
    } else {
      setOrder({
        order: KEY_ORDER.ASC,
        orderBy: column,
      });
    }
  };

  useEffect(() => {
    setCvVideosCount(meta?.itemCount);
    setCvVideosPendingCount(meta?.cvVideoPendingCount);
  }, [meta?.itemCount]);

  useEffect(() => {
    if (!hash) return;

    const defaultStatus: ISearchFilter = {
      field: FilterCriteriaFields.CVVIDEO_STATUS,
      value: defaultStatusValue,
    };
    setSearchFilter([defaultStatus]);
  }, [hash]);

  return (
    <>
      <Stack marginY={3} direction="row" gap={2}>
        <JSelect
          data-qa="select-status"
          defaultValue={defaultStatusValue || CvVideoStatus.PENDING}
          options={[
            { label: 'Tous', value: '' },
            { label: t('ACCEPTED_CV'), value: CvVideoStatus.ACCEPTED },
            { label: 'À vérifier', value: CvVideoStatus.PENDING },
            { label: t('REJECTED_CV'), value: CvVideoStatus.REJECTED },
            { label: t('PROCESSING_CV'), value: CvVideoStatus.PROCESSING },
          ]}
          size="small"
          label="Statut"
          onChange={(event) =>
            appendSearchFilter(
              FilterCriteriaFields.CVVIDEO_STATUS,
              event.target.value as string,
            )
          }
        />

        <SearchTextField
          data-qa="full-name"
          label={t('FULL_NAME') ?? undefined}
          onChange={(value) => {
            debouncedAppendSearchFilter(
              FilterCriteriaFields.USER_FULLNAME,
              value,
            );
          }}
        />

        <JAutocomplete
          data-qa="country"
          label={t('COUNTRY')}
          options={countriesOptions}
          onChange={(ev, newValue) => {
            setCountryId((newValue as IOption)?.value || '');
            appendSearchFilter(FilterCriteriaFields.USER_CITY_ID, undefined);
            appendSearchFilter(FilterCriteriaFields.USER_CITY_NAME, undefined);
          }}
          onTextChange={(val) => debouncedCountry(val)}
        />
        {countryId === FRANCE_COUNTRY_ID && (
          <JAutocomplete
            data-qa="city"
            label={t('CITY_ZIP_CODE')}
            options={citiesOptions}
            onChange={(ev, newValue) =>
              appendSearchFilter(
                FilterCriteriaFields.USER_CITY_ID,
                (newValue as IOption)?.value || '',
              )
            }
            onTextChange={(val) => debouncedCity(val)}
          />
        )}

        {countryId !== FRANCE_COUNTRY_ID && (
          <SearchTextField
            data-qa="city-name"
            label={t('CITY') ?? undefined}
            onChange={(value) => {
              debouncedAppendSearchFilter(
                FilterCriteriaFields.USER_CITY_NAME,
                value,
              );
            }}
          />
        )}
      </Stack>
      <JTable
        isNotFound={isNotFound}
        columns={CV_VIDEO_TABLE_COLS}
        notFoundLabel={t('NO_CV_VIDEO_FOUND') ?? ''}
        total={meta?.itemCount ?? 0}
        perpage={CV_VIDEO_PER_PAGE}
        nbpages={pagesCount ?? 0}
        onChangeOrder={(value) => handleColumnClick(value)}
      >
        {rows?.map((row) => (
          <CvVideoRow
            data-qa={`cv-video-row-${row.name}`}
            row={row}
            key={row.id}
            filterStatus={
              searchFilter.find(
                (item) => item.field === FilterCriteriaFields.CVVIDEO_STATUS,
              )?.value
            }
          />
        ))}
      </JTable>

      {meta && pagesCount && pagesCount > 1 ? (
        <Box mt={2}>
          <JTablePagination
            onPageChange={(value) => {
              setPage(value);
            }}
            meta={meta}
          />
        </Box>
      ) : (
        <></>
      )}
    </>
  );
};

export default CVVideoList;
